import { caseTaskSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import { mediaSchema } from "@/components/Forms/schema/mediaSchema.ts";
import { noteSchema } from "@/components/Forms/schema/note/noteSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector } from "jsonapi-zod-query";
import { z } from "zod";

const notifiedUserDetailSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: z.string().nullable(),
    notifiedAt: z.string().nullable(),
    isRead: z.boolean(),
    readAt: z.string().nullable(),
});

const companionCaseSchema = z.object({
    caseId: z.string(),
    caseNumber: z.string(),
    clientName: z.string(),
    caseType: z.string(),
});

const noteDetailsAttributesSchema = noteSchema.extend({
    notifiedUsersDetailed: z.array(notifiedUserDetailSchema).optional().default([]),
    companionCases: z.array(companionCaseSchema).optional().default([]),
});

const resourceSelector = createResourceSelector({
    type: "note",
    attributesSchema: noteDetailsAttributesSchema,
    relationships: {
        caseTask: {
            relationshipType: "one_nullable",
            include: {
                type: "caseTask",
                attributesSchema: caseTaskSchema,
            },
        },
        media: {
            relationshipType: "many",
            include: {
                type: "media",
                attributesSchema: mediaSchema,
            },
        },
    },
});

export const noteDetailsSelector = createDataSelector(resourceSelector);
export type NoteDetails = ReturnType<typeof noteDetailsSelector>;
export type CompanionCase = z.infer<typeof companionCaseSchema>;
export type NotifiedUserDetail = z.infer<typeof notifiedUserDetailSchema>;

type Props = {
    noteId: string | null;
};

export const useFetchNoteDetails = ({ noteId }: Props): UseQueryResult<NoteDetails> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["note-details", noteId],
        queryFn: async ({ signal }) => {
            const response = await fetch(apiUrl(`/note/details/${noteId}`), {
                signal,
                headers: {
                    accept: "application/vnd.api+json",
                },
            });

            return await response.json();
        },
        select: noteDetailsSelector,
        enabled: !!noteId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};
